/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback, useMemo, useState } from 'react';
import { Link, TypographyProps, Typography } from '@mui/material';
import sanitizeHtml from 'sanitize-html';

export interface BaseReadMoreProps extends Omit<TypographyProps, 'component'> {
  value: string;
  charLimit?: number;
  ellipses?: string;
  showDoubleQuotes?: boolean;
  onShowMoreClick?: () => void;
  onShowLessClick?: () => void;
  showMoreLabel?: string;
  component?: React.ElementType;
}

export interface WithShowLessLabel extends BaseReadMoreProps {
  showLessLabel: string;
  displayShowLessButton: true;
}

export interface WithoutShowLessLabel extends BaseReadMoreProps {
  showLessLabel?: string;
  displayShowLessButton?: never;
}

export type ReadMoreProps = WithShowLessLabel | WithoutShowLessLabel;

const SPACE_CHAR = ' ';
const WHITE_SPACE_REGEXP = /\s*$/;
const DOUBLE_QUOTES = '"';

export const splitValueIntoParts = (value: string, charLimit: number) => {
  if (!value) {
    return {
      teaser: value,
    };
  }

  const remainingWords = [];
  const teaserWords = value.split(SPACE_CHAR);

  while (teaserWords.join(SPACE_CHAR).length > charLimit) {
    remainingWords.unshift(teaserWords.pop());
  }

  return {
    teaser: teaserWords.join(SPACE_CHAR),
    remaining: remainingWords.join(SPACE_CHAR),
  };
};

export const ReadMore: React.FC<ReadMoreProps> = ({
  value,
  charLimit = 500,
  ellipses = '...',
  displayShowLessButton = true,
  showDoubleQuotes = false,
  onShowMoreClick = () => {},
  onShowLessClick = () => {},
  showMoreLabel = 'more',
  showLessLabel = 'less',
  ...props
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const handleToggle = useCallback(() => {
    setIsExpanded(!isExpanded);

    if (isExpanded) {
      onShowLessClick();
    } else {
      onShowMoreClick();
    }
  }, [isExpanded, onShowMoreClick, onShowLessClick]);

  const { teaser, remaining } = useMemo(
    () => splitValueIntoParts(value, charLimit),
    [value, charLimit]
  );

  const canBeToggled = value.length > charLimit;

  const splitContent = !isExpanded
    ? `${teaser.replace(WHITE_SPACE_REGEXP, '')}`
    : `${teaser} ${remaining}`;

  const content = canBeToggled ? splitContent : value;

  const toggleLabel = isExpanded ? `${showLessLabel}` : `${showMoreLabel}`;
  const hideToggle = canBeToggled && (!isExpanded || displayShowLessButton);

  return (
    <Typography {...props}>
      {showDoubleQuotes && `${DOUBLE_QUOTES}`}
      {content}
      {showDoubleQuotes && !hideToggle && `${DOUBLE_QUOTES}`}
      {/* eslint-disable-next-line react/no-danger */}
      <noscript dangerouslySetInnerHTML={{ __html: remaining ? sanitizeHtml(remaining) : '' }} />
      {hideToggle && (
        <>
          {isExpanded && showDoubleQuotes && `${DOUBLE_QUOTES}`}
          {isExpanded && `${SPACE_CHAR}`}
          {!isExpanded && `${ellipses}${SPACE_CHAR}`}
          <Link
            sx={{
              font: 'inherit',
              verticalAlign: 'inherit',
              textDecoration: 'underline',
            }}
            component="button"
            onClick={handleToggle}>
            {toggleLabel}
          </Link>
        </>
      )}
    </Typography>
  );
};
